import { useTranslation } from "react-i18next";
import { DeleteOutlined, PaperClipOutlined } from "@ant-design/icons";
import { createStyles } from "antd-style";

import type { UploadFileExtended } from "@av/apps/public/jobs/services/apply/types";
import {
  type DocumentUploadValidationError,
  resolveDocumentUploadValidationErrorTranslation,
} from "@av/apps/recruitment/ats-common/services/document-upload-validation";
import { Button } from "@av/components/atoms/Button";
import { Typography } from "@av/components/atoms/typography/Typography";
import { MiddleEllipsisFileName } from "./MiddleEllipsisFileName";

const useStyles = createStyles(({ css }) => ({
  fileName: css`
    min-width: 0;
    padding: 0;
    color: inherit;
  `,
}));

type UploadedDocumentModalFileRowProps = {
  file: Pick<UploadFileExtended, "uid" | "name">;
  validationError?: DocumentUploadValidationError;
  validationErrorId?: string;
  onRemove: () => void;
};

export function UploadedDocumentModalFileRow({
  file,
  validationError,
  validationErrorId,
  onRemove,
}: UploadedDocumentModalFileRowProps) {
  const { t } = useTranslation();
  const { styles } = useStyles();

  return (
    <>
      <div className="ant-upload-list-item ant-upload-list-item-done">
        <div className="ant-upload-icon">
          <PaperClipOutlined aria-hidden />
        </div>
        <div className="ant-upload-list-item-name">
          <MiddleEllipsisFileName
            className={styles.fileName}
            fileName={file.name}
            testIdPrefix={`supporting-document-${file.uid}-name`}
          />
        </div>
        <span className="ant-upload-list-item-actions">
          <Button
            testIdPrefix={`supporting-document-${file.uid}-remove`}
            className="ant-upload-list-item-action"
            type="text"
            size="small"
            icon={<DeleteOutlined />}
            aria-label={t("recruitment.jobApplication.documents_removeDocument", {
              fileName: file.name,
            })}
            onClick={onRemove}
          />
        </span>
      </div>
      {validationError ? (
        <Typography.Text
          id={validationErrorId}
          testIdPrefix={`supporting-document-${file.uid}-error`}
          role="alert"
          type="danger"
        >
          {resolveDocumentUploadValidationErrorTranslation(t, validationError)}
        </Typography.Text>
      ) : null}
    </>
  );
}
